import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { motion, AnimatePresence } from 'framer-motion';
import { ProjectMedia } from '../types';
import type SwiperType from 'swiper';

import 'swiper/css';
import 'swiper/css/pagination';

interface MediaGalleryProps {
  media: ProjectMedia[];
  title?: string;
  category?: string;
}

const BlurredLoader: React.FC<{ src: string }> = ({ src }) => (
  <div style={{ position: 'absolute', inset: 0, zIndex: 1, overflow: 'hidden' }}>
    <img
      src={src} alt=""
      style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(30px) brightness(0.6)', transform: 'scale(1.2)' }}
    />
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="36" height="36" viewBox="0 0 36 36" style={{ animation: 'spin 1s linear infinite' }}>
        <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2" strokeDasharray="60 25" strokeLinecap="round" />
      </svg>
    </div>
  </div>
);

/* ─── Fullscreen Gallery ─── */
const FullscreenGallery: React.FC<{
  media: ProjectMedia[];
  startIndex: number;
  onClose: () => void;
}> = ({ media, startIndex, onClose }) => {
  const [current, setCurrent] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPinching, setIsPinching] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);
  const touchStartDistance = useRef<number>(0);
  const lastScale = useRef<number>(1);
  const lastTap = useRef<number>(0);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const dragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const swiperRef = useRef<SwiperType | null>(null);

  // Clamp position so image can't go fully off-screen
  const clampPosition = useCallback((x: number, y: number, s: number) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Allow dragging up to half the scaled overflow beyond viewport
    const maxX = Math.max(0, (vw * (s - 1)) / 2);
    const maxY = Math.max(0, (vh * (s - 1)) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  }, []);

  // Block/unblock Swiper when zooming
  useEffect(() => {
    if (swiperRef.current) {
      const zoomed = scale > 1;
      swiperRef.current.allowSlideNext = !zoomed;
      swiperRef.current.allowSlidePrev = !zoomed;
      swiperRef.current.allowTouchMove = !zoomed;
    }
  }, [scale]);

  // Double tap to zoom
  const handleImageTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTap.current < 300 && lastTap.current > 0) {
      if (scale > 1) {
        setScale(1);
        setPosition({ x: 0, y: 0 });
        dragOffset.current = { x: 0, y: 0 };
      } else {
        setScale(2.5);
      }
      lastTap.current = 0;
    } else {
      lastTap.current = now;
    }
  }, [scale]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      setIsPinching(true);
      setIsDragging(false);
      dragStart.current = null;
      const d = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY
      );
      touchStartDistance.current = d;
      lastScale.current = scale;
    } else if (e.touches.length === 1 && scale > 1) {
      setIsDragging(true);
      dragStart.current = {
        x: e.touches[0].clientX - dragOffset.current.x,
        y: e.touches[0].clientY - dragOffset.current.y,
      };
    }
  }, [scale]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && isPinching) {
      const d = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY
      );
      const newScale = Math.min(Math.max((d / touchStartDistance.current) * lastScale.current, 1), 5);
      setScale(newScale);
      // Re-clamp position for new scale
      const clamped = clampPosition(dragOffset.current.x, dragOffset.current.y, newScale);
      dragOffset.current = clamped;
      setPosition(clamped);
    } else if (e.touches.length === 1 && dragStart.current && scale > 1) {
      const rawX = e.touches[0].clientX - dragStart.current.x;
      const rawY = e.touches[0].clientY - dragStart.current.y;
      const clamped = clampPosition(rawX, rawY, scale);
      dragOffset.current = clamped;
      setPosition(clamped);
    }
  }, [isPinching, scale, clampPosition]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    if (isPinching) {
      setIsPinching(false);
      if (scale <= 1) {
        setScale(1);
        setPosition({ x: 0, y: 0 });
        dragOffset.current = { x: 0, y: 0 };
      }
    }
    dragStart.current = null;
  }, [isPinching, scale]);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setCurrent(swiper.activeIndex);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    dragOffset.current = { x: 0, y: 0 };
    dragStart.current = null;
    lastTap.current = 0;
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header: close + counter */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10001,
      }}>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
          style={{
            width: '40px', height: '40px', borderRadius: '50%',
            border: '0.5px solid rgba(255,255,255,0.15)',
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        {media.length > 1 && (
          <div style={{
            fontSize: '14px', fontWeight: 500, color: 'rgba(255,255,255,0.6)',
            fontFamily: "'Instrument Sans', -apple-system, sans-serif",
          }}>
            {current + 1} / {media.length}
          </div>
        )}
        <div style={{ width: '40px' }} />
      </div>

      {/* Swipeable gallery */}
      <Swiper
        initialSlide={startIndex}
        spaceBetween={0}
        slidesPerView={1}
        onSwiper={(swiper: SwiperType) => { swiperRef.current = swiper; }}
        onSlideChange={handleSlideChange}
        allowTouchMove={scale === 1}
        style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
      >
        {media.map((item, idx) => (
          <SwiperSlide key={item.id}>
            <div
              ref={idx === current ? imageRef : null}
              style={{
                width: '100%', height: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                touchAction: scale > 1 ? 'none' : 'pan-y',
              }}
              onTouchStart={idx === current ? (e) => {
                handleTouchStart(e);
                if (scale > 1) e.stopPropagation();
              } : undefined}
              onTouchMove={idx === current ? (e) => {
                handleTouchMove(e);
                if (scale > 1 || e.touches.length === 2) {
                  e.stopPropagation();
                  e.preventDefault();
                }
              } : undefined}
              onTouchEnd={idx === current ? handleTouchEnd : undefined}
            >
              {item.media_type === 'video' ? (
                <video
                  src={item.url}
                  muted autoPlay loop playsInline
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : (
                <div
                  onClick={idx === current ? handleImageTap : undefined}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: scale > 1 ? 'grab' : 'default',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={item.url}
                    alt=""
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      transform: idx === current
                        ? `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`
                        : 'scale(1)',
                      transformOrigin: 'center center',
                      transition: (isPinching || isDragging) ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)',
                      willChange: 'transform',
                    }}
                    draggable={false}
                  />
                </div>
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </motion.div>
  );
};

const VideoSlide: React.FC<{ item: ProjectMedia }> = ({ item }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [loaded, setLoaded] = useState(false);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', background: '#000' }}>
      <AnimatePresence>
        {!loaded && (
          <motion.div key="loader" initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
            <BlurredLoader src={item.url} />
          </motion.div>
        )}
      </AnimatePresence>
      <motion.video
        ref={videoRef} src={item.url} muted autoPlay loop playsInline preload="metadata"
        onCanPlay={() => setLoaded(true)}
        initial={{ opacity: 0 }} animate={{ opacity: loaded ? 1 : 0 }} transition={{ duration: 0.5 }}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </div>
  );
};

const ImageSlide: React.FC<{
  item: ProjectMedia;
  onTap: (index: number) => void;
  index: number;
}> = ({ item, onTap, index }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      style={{ width: '100%', height: '100%', position: 'relative', background: '#000', cursor: 'pointer' }}
      onClick={() => { if (loaded) onTap(index); }}
    >
      <AnimatePresence>
        {!loaded && (
          <motion.div key="loader" initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}>
            <BlurredLoader src={item.url} />
          </motion.div>
        )}
      </AnimatePresence>
      <motion.img
        src={item.url} alt=""
        onLoad={() => setLoaded(true)}
        initial={{ opacity: 0, scale: 1.03 }}
        animate={{ opacity: loaded ? 1 : 0, scale: loaded ? 1 : 1.03 }}
        transition={{ duration: 0.5 }}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </div>
  );
};

const MediaGallery: React.FC<MediaGalleryProps> = ({ media, title, category }) => {
  const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  if (!media || media.length === 0) {
    return (
      <div style={{
        width: '100%', height: '420px', backgroundColor: '#F6F6F7',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#AEAEB2', position: 'relative',
      }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
        </svg>
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, padding: '80px 24px 24px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)',
        }}>
          {category && <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: '8px' }}>{category}</div>}
          {title && <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: '28px', lineHeight: 1.15, letterSpacing: '-0.03em', color: '#FFF', margin: 0 }}>{title}</h1>}
        </div>
      </div>
    );
  }

  const sortedMedia = [...media].sort((a, b) => a.order - b.order);
  const imageMedia = sortedMedia.filter(m => m.media_type === 'image');

  const handleImageTap = (slideIndex: number) => {
    // Find index in imageMedia array for fullscreen
    const item = sortedMedia[slideIndex];
    if (item && item.media_type === 'image') {
      const fsIndex = imageMedia.findIndex(m => m.id === item.id);
      setFullscreenIndex(fsIndex >= 0 ? fsIndex : 0);
    }
  };

  return (
    <>
      <div style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
        <Swiper
          modules={[Pagination]}
          pagination={{ clickable: true }}
          spaceBetween={0}
          slidesPerView={1}
          onSlideChange={(swiper: SwiperType) => setActiveIndex(swiper.activeIndex)}
          style={{ width: '100%', height: '420px' }}
        >
          {sortedMedia.map((item, idx) => (
            <SwiperSlide key={item.id} style={{ height: '420px' }}>
              {item.media_type === 'video' ? (
                <VideoSlide item={item} />
              ) : (
                <ImageSlide item={item} onTap={() => handleImageTap(idx)} index={idx} />
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Gradient overlay with category + title */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, padding: '80px 24px 24px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)',
          zIndex: 10, pointerEvents: 'none',
        }}>
          {category && <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: '8px' }}>{category}</div>}
          {title && <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: '28px', lineHeight: 1.15, letterSpacing: '-0.03em', color: '#FFF', margin: 0 }}>{title}</h1>}
        </div>

        {sortedMedia.length > 1 && (
          <div style={{
            position: 'absolute', top: '16px', right: '16px', fontSize: '12px', fontWeight: 500,
            color: 'rgba(255,255,255,0.7)', background: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
            padding: '4px 10px', borderRadius: '100px', zIndex: 10,
          }}>
            {activeIndex + 1} / {sortedMedia.length}
          </div>
        )}

        <style>{`
          .swiper-pagination-bullet {
            background: rgba(255,255,255,0.4) !important; width: 6px !important;
            height: 6px !important; opacity: 1 !important; transition: all 0.3s ease !important;
          }
          .swiper-pagination-bullet-active {
            background: #FFFFFF !important; width: 18px !important; border-radius: 3px !important;
          }
        `}</style>
      </div>

      {/* Fullscreen swipeable gallery — images only */}
      <AnimatePresence>
        {fullscreenIndex !== null && imageMedia.length > 0 && (
          <FullscreenGallery
            media={imageMedia}
            startIndex={fullscreenIndex}
            onClose={() => setFullscreenIndex(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default MediaGallery;
